import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const SENSORY = '#2493A2';
const INTEGRATION = '#57BDA2';
const CAPSTONE = '#E85D75';

export function PredictionVsInput() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const height = 160;
    const margin = { top: 20, right: 15, bottom: 30, left: 15 };
    const plotW = width - margin.left - margin.right;
    const plotH = height - margin.top - margin.bottom;
    const midY = margin.top + plotH / 2;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left}, 0)`);

    // --- Generate predicted (smooth) and actual (jagged) signal data ---
    const nPoints = 80;
    const xScale = d3
      .scaleLinear()
      .domain([0, nPoints - 1])
      .range([0, plotW]);

    const predictedData: [number, number][] = d3.range(nPoints).map((i) => {
      const x = xScale(i);
      const y =
        midY +
        Math.sin(i * 0.12) * 22 +
        Math.sin(i * 0.05) * 12;
      return [x, y];
    });

    const actualData: [number, number][] = d3.range(nPoints).map((i) => {
      const x = xScale(i);
      const y =
        midY +
        Math.sin(i * 0.12) * 22 +
        Math.sin(i * 0.05) * 12 +
        Math.sin(i * 0.4) * 8 +
        (Math.random() - 0.5) * 12;
      return [x, y];
    });

    const line = d3
      .line<[number, number]>()
      .x((d) => d[0])
      .y((d) => d[1])
      .curve(d3.curveBasis);

    const area = d3
      .area<number>()
      .x((_, i) => xScale(i))
      .y0((_, i) => predictedData[i][1])
      .y1((_, i) => actualData[i][1])
      .curve(d3.curveBasis);

    // --- Error shaded area ---
    const errorArea = g
      .append('path')
      .datum(d3.range(nPoints))
      .attr('d', area)
      .attr('fill', CAPSTONE)
      .attr('opacity', 0);

    // --- Predicted line (smooth) ---
    const predictedPath = g
      .append('path')
      .datum(predictedData)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', SENSORY)
      .attr('stroke-width', 2)
      .attr('opacity', 0.9);

    // Animate draw-in for predicted
    const predLength = (predictedPath.node() as SVGPathElement).getTotalLength();
    predictedPath
      .attr('stroke-dasharray', `${predLength} ${predLength}`)
      .attr('stroke-dashoffset', predLength)
      .transition()
      .duration(600)
      .ease(d3.easeLinear)
      .attr('stroke-dashoffset', 0);

    // --- Actual line (jagged) ---
    const actualPath = g
      .append('path')
      .datum(actualData)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', INTEGRATION)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4 2')
      .attr('opacity', 0.8);

    // Animate draw-in for actual (slight delay)
    const actLength = (actualPath.node() as SVGPathElement).getTotalLength();
    actualPath
      .attr('stroke-dasharray', `${actLength} ${actLength}`)
      .attr('stroke-dashoffset', actLength)
      .transition()
      .delay(200)
      .duration(600)
      .ease(d3.easeLinear)
      .attr('stroke-dashoffset', 0)
      .on('end', function () {
        // Restore dashed pattern after draw-in
        d3.select(this).attr('stroke-dasharray', '4 2');
      });

    // --- Show error area after lines drawn ---
    const t1 = d3.timeout(() => {
      errorArea
        .transition()
        .duration(500)
        .attr('opacity', 0.18);
    }, 1000);

    // --- Legend / labels ---
    const legendY = height - 10;

    const predLabel = g
      .append('g')
      .attr('opacity', 0);

    predLabel
      .append('line')
      .attr('x1', 10)
      .attr('y1', legendY)
      .attr('x2', 30)
      .attr('y2', legendY)
      .attr('stroke', SENSORY)
      .attr('stroke-width', 2);

    predLabel
      .append('text')
      .attr('x', 34)
      .attr('y', legendY + 3)
      .attr('font-size', 8)
      .attr('class', 'viz-text-muted')
      .text('predicted');

    const actLabel = g
      .append('g')
      .attr('opacity', 0);

    actLabel
      .append('line')
      .attr('x1', 90)
      .attr('y1', legendY)
      .attr('x2', 110)
      .attr('y2', legendY)
      .attr('stroke', INTEGRATION)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4 2');

    actLabel
      .append('text')
      .attr('x', 114)
      .attr('y', legendY + 3)
      .attr('font-size', 8)
      .attr('class', 'viz-text-muted')
      .text('actual');

    const errLabel = g
      .append('g')
      .attr('opacity', 0);

    errLabel
      .append('rect')
      .attr('x', 160)
      .attr('y', legendY - 5)
      .attr('width', 16)
      .attr('height', 10)
      .attr('fill', CAPSTONE)
      .attr('opacity', 0.35)
      .attr('rx', 2);

    errLabel
      .append('text')
      .attr('x', 180)
      .attr('y', legendY + 3)
      .attr('font-size', 8)
      .attr('class', 'viz-text-muted')
      .text('error');

    // --- "Error" annotation in the plot ---
    const errorAnnotation = g
      .append('text')
      .attr('x', plotW * 0.55)
      .attr('y', midY - 2)
      .attr('text-anchor', 'middle')
      .attr('font-size', 10)
      .attr('font-weight', 600)
      .attr('fill', CAPSTONE)
      .attr('opacity', 0)
      .text('error');

    const t2 = d3.timeout(() => {
      predLabel.transition().duration(300).attr('opacity', 1);
      actLabel.transition().duration(300).delay(100).attr('opacity', 1);
      errLabel.transition().duration(300).delay(200).attr('opacity', 1);

      errorAnnotation
        .transition()
        .duration(400)
        .attr('opacity', 0.7);
    }, 1300);

    return () => {
      t1.stop();
      t2.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-brain" /> Brain &mdash; Prediction Engine
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 160" />
      <p className="viz-container__caption">
        The brain minimizes error, not maximizes truth.
      </p>
    </div>
  );
}
